import type { CSSProperties } from "react";

type DocumentationSection = {
  id: string;
  title: string;
  purpose: string;
  useWhen: string[];
  howToUse: string[];
  managerNotes: string[];
};

const pageStyle: CSSProperties = {
  display: "grid",
  gap: 18,
  width: "100%",
  maxWidth: 1500,
  margin: "0 auto",
};

const sectionStyle: CSSProperties = {
  border: "1px solid #e2e8f0",
  borderRadius: 0,
  background: "#fff",
  padding: 18,
};

const smallLabelStyle: CSSProperties = {
  margin: 0,
  color: "#64748b",
  fontSize: 12,
  fontWeight: 900,
  letterSpacing: ".08em",
  textTransform: "uppercase",
};

const DOC_SECTIONS: DocumentationSection[] = [
  {
    id: "dashboard",
    title: "Dashboard",
    purpose:
      "The dashboard is the daily command center. It gives managers a fast read on sales, orders, support activity, email lead alerts, and live store operations.",
    useWhen: [
      "Start of day store review.",
      "Checking whether orders, chats, or email leads need attention.",
      "Reviewing high-level performance before opening deeper menu pages.",
    ],
    howToUse: [
      "Scan the top metrics first, then open the related page for any number that needs action.",
      "Use unread support and email lead indicators as operational alerts.",
      "Treat the dashboard as a summary, not as the final place to make detailed edits.",
    ],
    managerNotes: [
      "If a metric looks wrong, check the source page before changing settings.",
      "Use dashboard changes as a signal to investigate orders, support, products, or promotions.",
    ],
  },
  {
    id: "products",
    title: "Products",
    purpose:
      "Products controls what customers can buy, customize, compare, and preview on the storefront.",
    useWhen: [
      "Create a new product.",
      "Update price, product copy, photos, variants, inventory, or customization settings.",
      "Add a YouTube product video and decide whether it should appear as the hover thumbnail.",
    ],
    howToUse: [
      "Open Products to review the catalog or New Product to create an item.",
      "Keep product names clear, prices current, and images specific to the item.",
      "For double-sided pendants, add a short YouTube preview when available so customers understand both sides.",
      "Use the product edit page to confirm customization options before publishing.",
    ],
    managerNotes: [
      "Do not publish a product without a clear primary image.",
      "Avoid changing product IDs or core product type after orders exist for that item.",
      "Use real product media whenever possible because product pages drive buying confidence.",
    ],
  },
  {
    id: "orders",
    title: "Orders",
    purpose:
      "Orders is where staff reviews purchases, customer details, customization files, fulfillment status, shipping details, and order notes.",
    useWhen: [
      "A new order arrives.",
      "A customer asks for status or customization help.",
      "Shipping, fulfillment, refund, or internal note updates are needed.",
    ],
    howToUse: [
      "Open the order, verify payment and customer contact details, then review customization previews.",
      "Use fulfillment fields consistently so staff can see what has already happened.",
      "Add internal notes for anything another admin should know before responding to the customer.",
    ],
    managerNotes: [
      "Never mark an order fulfilled until production and shipping steps are actually complete.",
      "If a customer image looks wrong, confirm before producing the item.",
    ],
  },
  {
    id: "referrals-coupons",
    title: "Referrals & Coupons",
    purpose:
      "This page owns referral rewards, coupon tokens, affiliate links, signup offer settings, stacking rules, and redemption history.",
    useWhen: [
      "Change whether customers can combine more than one promo discount.",
      "Create or edit a coupon token.",
      "Issue or test referral rewards.",
      "Review coupon redemption history and affiliate token behavior.",
    ],
    howToUse: [
      "Use Referral Settings for program-wide rules like reward amount, expiration, and stacking.",
      "Use Coupon & Affiliate Tokens to create customer-facing codes and creator affiliate codes.",
      "Use conditions when a coupon should only apply to first orders, order count, spend amount, or spend window.",
      "Keep email list management out of this page; use Email Leads for audience selection and sending.",
    ],
    managerNotes: [
      "Promo stacking is off by default to protect margin.",
      "Do not create public all-user coupon codes unless you are comfortable with anyone using them.",
      "Use short, readable codes that staff can recognize in support conversations.",
    ],
  },
  {
    id: "email-leads",
    title: "Email Leads",
    purpose:
      "Email Leads is only for captured email addresses, audience filters, selection, template previews, and careful one-off email sends.",
    useWhen: [
      "Review emails captured from the storefront prompt.",
      "Filter leads by registration date, order count, spend, conversion state, email status, or signup source.",
      "Send a small campaign to selected leads or all leads matching current filters.",
    ],
    howToUse: [
      "Filter the list first, then select individual rows or choose to send to all matching filters.",
      "Pick one of the three templates to see a small preview, then apply it to load the subject and message.",
      "Review the full email preview before sending.",
      "Keep the message useful, accurate, and quiet. Avoid spammy wording, misleading offers, and excessive punctuation.",
    ],
    managerNotes: [
      "Filter sends are capped to protect deliverability.",
      "Use selected sends for small manual follow-ups.",
      "If email sending fails, verify SMTP credentials and sender configuration before retrying.",
    ],
  },
  {
    id: "reviews",
    title: "Reviews & Review Codes",
    purpose:
      "Reviews pages let admins moderate customer reviews and manage review-related reward codes.",
    useWhen: [
      "Approve, reject, or inspect customer reviews.",
      "Create or audit review codes used for post-order reward flows.",
      "Check whether review content is appropriate before it appears publicly.",
    ],
    howToUse: [
      "Review submitted content for accuracy, tone, and customer privacy.",
      "Approve real customer reviews that meet store standards.",
      "Use Review Codes when the business needs controlled review reward distribution.",
    ],
    managerNotes: [
      "Do not edit a review in a way that changes the customer's meaning.",
      "Reject content that exposes private information or looks fraudulent.",
    ],
  },
  {
    id: "support",
    title: "Support Chat",
    purpose:
      "Support Chat is for real-time customer conversations, order questions, customization help, and issue resolution.",
    useWhen: [
      "A customer sends a live chat message.",
      "Staff needs to answer customization, shipping, or order questions.",
      "A thread needs manager review before a response.",
    ],
    howToUse: [
      "Open unread threads first.",
      "Check order details before answering order-specific questions.",
      "Keep replies clear and short, then add internal notes elsewhere if staff context is needed.",
    ],
    managerNotes: [
      "If chat access is denied in dev, update Firestore rules for supportThreads/user_<uid>, including messages and typing subcollections.",
      "Do not ask customers for sensitive payment details in chat.",
    ],
  },
  {
    id: "website-visits",
    title: "Website Visits",
    purpose:
      "Website Visits shows visitor activity, page views, attribution details, and captured email context so managers can understand customer behavior.",
    useWhen: [
      "Investigate traffic source quality.",
      "Review page activity around email capture or conversion behavior.",
      "Look for patterns before changing product pages or promotions.",
    ],
    howToUse: [
      "Use filters to narrow visits by page, source, campaign, or date.",
      "Compare page visits with captured emails and orders before making decisions.",
      "Use this page for behavior analysis, not for sending emails.",
    ],
    managerNotes: [
      "Visitor tracking is directional. Use it with order and lead data before making large decisions.",
      "Respect customer privacy and avoid exporting data unnecessarily.",
    ],
  },
  {
    id: "seo",
    title: "SEO Dashboard",
    purpose:
      "SEO Dashboard helps admins review search readiness, sitemap status, metadata, and search-related storefront health.",
    useWhen: [
      "After product or content changes.",
      "Before launching new product categories.",
      "When checking whether search metadata is complete.",
    ],
    howToUse: [
      "Review missing or weak metadata.",
      "Check sitemap and indexing-related status after content changes.",
      "Keep product titles and descriptions readable for humans first, then optimize search details.",
    ],
    managerNotes: [
      "SEO updates can take time to appear in search engines.",
      "Do not stuff keywords into customer-facing content.",
    ],
  },
  {
    id: "content",
    title: "Content Studio",
    purpose:
      "Content Studio controls storefront content, homepage sections, brand visuals, and operational copy managed by admins.",
    useWhen: [
      "Change homepage content, brand text, banners, or visual settings.",
      "Update global content without editing code.",
      "Prepare seasonal content changes.",
    ],
    howToUse: [
      "Edit one content area at a time and review the storefront after saving.",
      "Keep copy concise and product-focused.",
      "Use real brand assets and avoid placeholder text in live content.",
    ],
    managerNotes: [
      "Large content changes should be reviewed on desktop and mobile.",
      "Do not overwrite brand assets unless the new files are final.",
    ],
  },
  {
    id: "operations-settings",
    title: "Other Settings",
    purpose:
      "Other Settings contains operational controls that affect store behavior outside normal catalog and order work.",
    useWhen: [
      "A super admin needs to change store-level operations.",
      "Settings affect broad customer-facing behavior.",
      "A deployment or launch requires operational toggles.",
    ],
    howToUse: [
      "Read the setting label and help text before changing it.",
      "Change only one operational setting at a time when debugging.",
      "Confirm the customer-facing result after saving.",
    ],
    managerNotes: [
      "Restrict this page to trusted super admins.",
      "Record why a major operational setting changed.",
    ],
  },
  {
    id: "admin-users",
    title: "Admin Users",
    purpose:
      "Admin Users controls invitations, roles, permissions, and who can access management tools.",
    useWhen: [
      "Invite a new manager or admin.",
      "Change a staff member's role.",
      "Audit who has access to super admin features.",
    ],
    howToUse: [
      "Invite users with the least access they need.",
      "Use manager roles for daily operations and super admin roles for sensitive settings.",
      "Remove or downgrade access when a staff member no longer needs it.",
    ],
    managerNotes: [
      "Access control is a security feature, not just a menu preference.",
      "Do not share admin accounts between staff members.",
    ],
  },
];

const WORKFLOWS = [
  {
    title: "Create A Product",
    steps: [
      "Go to Products or New Product.",
      "Add name, price, description, product media, and customization settings.",
      "For pendants, confirm photo placement options and add a YouTube preview when useful.",
      "Save, then check the customer product page on desktop and mobile.",
    ],
  },
  {
    title: "Fulfill An Order",
    steps: [
      "Open Orders and select the newest paid order.",
      "Review customer details, item details, and customization preview.",
      "Update production, fulfillment, and shipping details as each step completes.",
      "Use support chat only for customer communication, not as the source of fulfillment truth.",
    ],
  },
  {
    title: "Send A Lead Email",
    steps: [
      "Open Email Leads.",
      "Filter the audience by date, order count, spend, conversion state, or source.",
      "Select individual emails or choose all matching filters.",
      "Pick a template, apply it, edit the subject and message, review the preview, then send.",
    ],
  },
  {
    title: "Launch A Coupon",
    steps: [
      "Open Referrals & Coupons.",
      "Confirm stacking rules and referral settings.",
      "Create the coupon or affiliate token with clear conditions.",
      "Review redemption history after launch to make sure usage matches expectations.",
    ],
  },
];

function BulletList({ items }: { items: string[] }) {
  return (
    <ul style={{ margin: "8px 0 0", paddingLeft: 18, color: "#334155", lineHeight: 1.55 }}>
      {items.map((item) => (
        <li key={item}>{item}</li>
      ))}
    </ul>
  );
}

export default function AdminDocumentationPage() {
  return (
    <div style={pageStyle} className="admin-docs-page">
      <style>{`
        .admin-docs-page a {
          color: #0f172a;
          text-decoration: none;
        }
        .admin-docs-page a:hover {
          text-decoration: underline;
        }
        .admin-docs-page table th,
        .admin-docs-page table td {
          padding: 10px;
          border-bottom: 1px solid #e2e8f0;
          text-align: left;
          vertical-align: top;
        }
        @media (max-width: 760px) {
          .admin-docs-page .docs-two-column {
            grid-template-columns: 1fr !important;
          }
          .admin-docs-page .docs-anchor-grid {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>

      <section style={sectionStyle}>
        <p style={smallLabelStyle}>Admin training</p>
        <h1 style={{ margin: "6px 0 0", color: "#0f172a", fontSize: 28 }}>
          Admin Documentation
        </h1>
        <p style={{ margin: "8px 0 0", color: "#475569", maxWidth: 920, lineHeight: 1.6 }}>
          This page is the master guide for new admins and managers. It explains what
          each admin menu does, when to use it, how to use it safely, and which workflows
          matter most during daily store operations.
        </p>
      </section>

      <section style={sectionStyle}>
        <h2 style={{ margin: 0, color: "#0f172a", fontSize: 20 }}>Quick Navigation</h2>
        <div
          className="docs-anchor-grid"
          style={{
            marginTop: 12,
            display: "grid",
            gap: 0,
            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
            border: "1px solid #e2e8f0",
            borderBottom: 0,
          }}
        >
          {DOC_SECTIONS.map((section) => (
            <a
              key={section.id}
              href={`#${section.id}`}
              style={{
                padding: 12,
                borderBottom: "1px solid #e2e8f0",
                borderRight: "1px solid #e2e8f0",
                fontWeight: 800,
              }}
            >
              {section.title}
            </a>
          ))}
        </div>
      </section>

      <section style={sectionStyle}>
        <h2 style={{ margin: 0, color: "#0f172a", fontSize: 20 }}>Common Workflows</h2>
        <div
          className="docs-two-column"
          style={{
            marginTop: 12,
            display: "grid",
            gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
            border: "1px solid #e2e8f0",
            borderBottom: 0,
          }}
        >
          {WORKFLOWS.map((workflow) => (
            <div
              key={workflow.title}
              style={{
                padding: 14,
                borderBottom: "1px solid #e2e8f0",
                borderRight: "1px solid #e2e8f0",
              }}
            >
              <h3 style={{ margin: 0, color: "#0f172a", fontSize: 16 }}>
                {workflow.title}
              </h3>
              <ol style={{ margin: "8px 0 0", paddingLeft: 18, color: "#334155", lineHeight: 1.55 }}>
                {workflow.steps.map((step) => (
                  <li key={step}>{step}</li>
                ))}
              </ol>
            </div>
          ))}
        </div>
      </section>

      <section style={sectionStyle}>
        <h2 style={{ margin: 0, color: "#0f172a", fontSize: 20 }}>
          Menu Documentation
        </h2>
        <p style={{ margin: "6px 0 0", color: "#64748b", fontSize: 13 }}>
          Each section below matches an admin menu area and can be read separately.
        </p>
      </section>

      {DOC_SECTIONS.map((section) => (
        <section key={section.id} id={section.id} style={sectionStyle}>
          <h2 style={{ margin: 0, color: "#0f172a", fontSize: 22 }}>{section.title}</h2>
          <p style={{ margin: "8px 0 0", color: "#334155", lineHeight: 1.6 }}>
            {section.purpose}
          </p>
          <div
            className="docs-two-column"
            style={{
              marginTop: 14,
              display: "grid",
              gap: 16,
              gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
            }}
          >
            <div>
              <p style={smallLabelStyle}>Use When</p>
              <BulletList items={section.useWhen} />
            </div>
            <div>
              <p style={smallLabelStyle}>How To Use</p>
              <BulletList items={section.howToUse} />
            </div>
            <div>
              <p style={smallLabelStyle}>Manager Notes</p>
              <BulletList items={section.managerNotes} />
            </div>
          </div>
        </section>
      ))}
    </div>
  );
}
